import React, { useEffect, useRef, useState } from 'react';
import { IconCheck, IconTrash, IconX } from '@tabler/icons-react';
import { Controller, useForm } from 'react-hook-form';
import type { User } from '../../../entities/user';
import { pb } from '../../../shared/api/pocketbase';
import { logAction } from '../../../shared/lib/userLogger';
import { Button } from '../../../shared/ui/Button';
import { UserSearchInput } from '../../../shared/ui/UserSearchInput/UserSearchInput';
import { EMPTY_DELETE_USER_FORM, type DeleteUserForm } from '../model/types';

interface DeleteUserModalProps {
  user: User;
  onClose: () => void;
  onDeleted: () => void;
}

interface Counts {
  tasks: number;
  jobs: number;
}

interface Progress {
  done: number;
  total: number;
  label: string;
  waitingSec?: number;
}

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const RATE_LIMIT_WAIT_MS = 30_000;


const DeleteUserModal: React.FC<DeleteUserModalProps> = ({ user, onClose, onDeleted }) => {
  const [counts, setCounts] = useState<Counts | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [progress, setProgress] = useState<Progress | null>(null);
  const [waitCountdown, setWaitCountdown] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const { control, handleSubmit, watch, setValue } = useForm<DeleteUserForm>({
    defaultValues: EMPTY_DELETE_USER_FORM,
  });

  const tasksAction = watch('tasksAction');
  const jobsAction = watch('jobsAction');
  const jobsLockedByTasks = tasksAction === 'delete';

  useEffect(() => {
    const loadCounts = async () => {
      const [tasksRes, jobsRes] = await Promise.all([
        pb.collection('tasks').getList(1, 1, {
          filter: `userId = "${user.id}"`,
          fields: 'id',
          requestKey: null,
        }),
        pb.collection('jobsTasks').getList(1, 1, {
          filter: `ownerId = "${user.id}"`,
          fields: 'id',
          requestKey: null,
        }),
      ]);
      setCounts({ tasks: tasksRes.totalItems, jobs: jobsRes.totalItems });
    };
    loadCounts().catch(() => setCounts({ tasks: 0, jobs: 0 }));
  }, [user.id]);

  useEffect(() => {
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const startCountdown = () =>
    new Promise<void>(resolve => {
      let sec = RATE_LIMIT_WAIT_MS / 1000;
      setWaitCountdown(sec);
      countdownRef.current = setInterval(() => {
        sec -= 1;
        if (sec <= 0) {
          clearInterval(countdownRef.current!);
          countdownRef.current = null;
          setWaitCountdown(null);
          resolve();
        } else {
          setWaitCountdown(sec);
        }
      }, 1000);
    });

  const runWithRateLimit = async <T,>(fn: () => Promise<T>): Promise<T> => {
    let attempts = 0;
    while (true) {
      try {
        return await fn();
      } catch (err: any) {
        if (err?.status === 429 && attempts < 5) {
          attempts += 1;
          await startCountdown();
          continue;
        }
        throw err;
      }
    }
  };

  const handleFormSubmit = handleSubmit(async (values) => {
    setError('');
    setDeleting(true);

    try {
      // --- Tasks ---
      if (values.tasksAction === 'delete') {
        setProgress({ done: 0, total: 0, label: 'Загружаем карточки...' });
        const allTasks = await runWithRateLimit(() =>
          pb.collection('tasks').getFullList({
            filter: `userId = "${user.id}"`,
            fields: 'id',
            requestKey: null,
          }),
        );
        const total = allTasks.length;
        for (let i = 0; i < allTasks.length; i++) {
          setProgress({ done: i, total, label: `Удаляем карточки: ${i} / ${total}` });
          const t = allTasks[i];
          const logs = await runWithRateLimit(() =>
            pb.collection('taskStageLogs').getFullList({
              filter: `taskId = "${t.id}"`,
              fields: 'id',
              requestKey: null,
            }),
          );
          for (const l of logs) {
            await runWithRateLimit(() => pb.collection('taskStageLogs').update(l.id, { actual: false }));
          }
          await runWithRateLimit(() => pb.collection('tasks').update(t.id, { actual: false }));
        }
        setProgress({ done: total, total, label: `Удаляем карточки: ${total} / ${total}` });
      } else if (values.tasksAction === 'transfer' && values.tasksTransferUserId) {
        setProgress({ done: 0, total: 0, label: 'Загружаем карточки...' });
        const allTasks = await runWithRateLimit(() =>
          pb.collection('tasks').getFullList({
            filter: `userId = "${user.id}"`,
            fields: 'id',
            requestKey: null,
          }),
        );
        const total = allTasks.length;
        for (let i = 0; i < allTasks.length; i++) {
          setProgress({ done: i, total, label: `Переносим карточки: ${i} / ${total}` });
          await runWithRateLimit(() =>
            pb.collection('tasks').update(allTasks[i].id, { userId: values.tasksTransferUserId }),
          );
        }
        setProgress({ done: total, total, label: `Переносим карточки: ${total} / ${total}` });
      }

      // --- Jobs ---
      if (values.jobsAction === 'delete' || jobsLockedByTasks) {
        setProgress({ done: 0, total: 0, label: 'Загружаем задания...' });
        const allJobs = await runWithRateLimit(() =>
          pb.collection('jobsTasks').getFullList({
            filter: `ownerId = "${user.id}"`,
            fields: 'id',
            requestKey: null,
          }),
        );
        const total = allJobs.length;
        for (let i = 0; i < allJobs.length; i++) {
          setProgress({ done: i, total, label: `Удаляем задания: ${i} / ${total}` });
          await runWithRateLimit(() => pb.collection('jobsTasks').update(allJobs[i].id, { actual: false }));
        }
        setProgress({ done: total, total, label: `Удаляем задания: ${total} / ${total}` });
      } else if (values.jobsAction === 'transfer' && values.jobsTransferUserId) {
        setProgress({ done: 0, total: 0, label: 'Загружаем задания...' });
        const allJobs = await runWithRateLimit(() =>
          pb.collection('jobsTasks').getFullList({
            filter: `ownerId = "${user.id}"`,
            fields: 'id',
            requestKey: null,
          }),
        );
        const total = allJobs.length;
        for (let i = 0; i < allJobs.length; i++) {
          setProgress({ done: i, total, label: `Переносим задания: ${i} / ${total}` });
          await runWithRateLimit(() =>
            pb.collection('jobsTasks').update(allJobs[i].id, { ownerId: values.jobsTransferUserId }),
          );
        }
        setProgress({ done: total, total, label: `Переносим задания: ${total} / ${total}` });
      }

      // --- Delete user ---
      setProgress({ done: 0, total: 1, label: 'Удаляем пользователя...' });
      await runWithRateLimit(() => pb.collection('users').update(user.id, { actual: false }));

      setProgress(null);
      logAction(`Deleted user: ${user.name || user.email}`, 'REMOVE');
      setDone(true);
    } catch {
      setError('Ошибка при удалении. Попробуйте ещё раз.');
    } finally {
      setDeleting(false);
    }
  });

  const progressPct =
    progress && progress.total > 0
      ? Math.round((progress.done / progress.total) * 100)
      : null;

  return (
    <div
      className="mo open"
      ref={overlayRef}
      onClick={e => e.target === overlayRef.current && !deleting && onClose()}
    >
      <div className="cm cm-create" style={{ maxWidth: 480, overflow: 'visible' }}>
        <div className="cm-top" style={{ borderRadius: '20px 20px 0 0' }}>
          <div className="cm-ti">Удалить пользователя</div>
          <div className="cm-sp" />
          {!deleting && (
            <button className="cm-x" onClick={onClose} type="button">
              <IconX size={16} stroke={2} />
            </button>
          )}
        </div>

        {done ? (
          <div
            className="cm-create-body"
            style={{ gap: 20, alignItems: 'center', textAlign: 'center', borderRadius: '0 0 20px 20px' }}
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, padding: '8px 0' }}>
              <div
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  background: 'var(--green, #22c55e)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <IconCheck size={28} stroke={2.5} color="#fff" />
              </div>
              <p style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
                Пользователь удалён
              </p>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--text-secondary)' }}>
                {user.name || user.email} успешно удалён из системы
              </p>
            </div>
            <div className="adm-form-actions">
              <Button variant="adm-cancel" type="button" onClick={onDeleted}>
                Закрыть
              </Button>
            </div>
          </div>
        ) : (
          <form className="cm-create-body" onSubmit={handleFormSubmit} style={{ gap: 20, overflow: 'visible', borderRadius: '0 0 20px 20px' }}>
            {deleting ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>
                  {progress?.label ?? 'Подготовка...'}
                </p>

                {progress && progress.total > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <div
                      style={{
                        height: 8,
                        borderRadius: 4,
                        background: 'var(--bg-tertiary, #e5e7eb)',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          height: '100%',
                          borderRadius: 4,
                          background: 'var(--accent, #6366f1)',
                          width: `${progressPct}%`,
                          transition: 'width 0.2s ease',
                        }}
                      />
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-secondary)', textAlign: 'right' }}>
                      {progressPct}%
                    </div>
                  </div>
                )}

                {waitCountdown !== null && (
                  <div
                    style={{
                      fontSize: 13,
                      color: 'var(--text-secondary)',
                      background: 'var(--bg-secondary, #f9fafb)',
                      borderRadius: 8,
                      padding: '8px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                    }}
                  >
                    <span className="adm-btn-spinner" style={{ flexShrink: 0 }} />
                    Превышен лимит запросов. Продолжаем через {waitCountdown} сек...
                  </div>
                )}
              </div>
            ) : (
              <>
                <p style={{ margin: 0, fontSize: 14, color: 'var(--text-secondary)' }}>
                  Удаление <strong>{user.name || user.email}</strong>. Выберите, что сделать с его данными.
                </p>

                {/* Tasks section */}
                <div className="adm-del-section">
                  <div className="adm-del-section-title">
                    Карточки на менеджере
                    {counts !== null && (
                      <span className="adm-del-count">{counts.tasks}</span>
                    )}
                  </div>
                  <label className="adm-del-radio">
                    <Controller
                      control={control}
                      name="tasksAction"
                      render={({ field }) => (
                        <input
                          type="radio"
                          value="delete"
                          checked={field.value === 'delete'}
                          onChange={() => field.onChange('delete')}
                        />
                      )}
                    />
                    Удалить все карточки
                  </label>
                  <label className="adm-del-radio">
                    <Controller
                      control={control}
                      name="tasksAction"
                      render={({ field }) => (
                        <input
                          type="radio"
                          value="transfer"
                          checked={field.value === 'transfer'}
                          onChange={() => field.onChange('transfer')}
                        />
                      )}
                    />
                    Перенести на другого менеджера
                  </label>
                  {tasksAction === 'transfer' && (
                    <div style={{ marginTop: 8 }}>
                      <Controller
                        control={control}
                        name="tasksTransferUserId"
                        rules={{ required: tasksAction === 'transfer' ? 'Выберите менеджера' : false }}
                        render={({ field, fieldState }) => (
                          <>
                            <UserSearchInput
                              name="tasks-transfer-user"
                              value={field.value}
                              selectedName={watch('tasksTransferUserName')}
                              onSelect={(id, name) => {
                                field.onChange(id);
                                setValue('tasksTransferUserName', name);
                              }}
                              onClear={() => {
                                field.onChange('');
                                setValue('tasksTransferUserName', '');
                              }}
                              placeholder="Выберите администратора"
                              includeBlocked
                            />
                            {fieldState.error && (
                              <div className="form-field-error">{fieldState.error.message}</div>
                            )}
                          </>
                        )}
                      />
                    </div>
                  )}
                </div>

                {/* Jobs section */}
                <div className={`adm-del-section${jobsLockedByTasks ? ' adm-del-section-locked' : ''}`}>
                  <div className="adm-del-section-title">
                    Задания пользователя
                    {counts !== null && (
                      <span className="adm-del-count">{counts.jobs}</span>
                    )}
                  </div>
                  {jobsLockedByTasks && (
                    <p className="adm-del-locked-hint">Будут удалены автоматически вместе с задачами</p>
                  )}
                  <label className={`adm-del-radio${jobsLockedByTasks ? ' adm-del-radio-disabled' : ''}`}>
                    <Controller
                      control={control}
                      name="jobsAction"
                      render={({ field }) => (
                        <input
                          type="radio"
                          value="delete"
                          checked={jobsLockedByTasks || field.value === 'delete'}
                          onChange={() => field.onChange('delete')}
                          disabled={jobsLockedByTasks}
                        />
                      )}
                    />
                    Удалить все задания
                  </label>
                  <label className={`adm-del-radio${jobsLockedByTasks ? ' adm-del-radio-disabled' : ''}`}>
                    <Controller
                      control={control}
                      name="jobsAction"
                      render={({ field }) => (
                        <input
                          type="radio"
                          value="transfer"
                          checked={!jobsLockedByTasks && field.value === 'transfer'}
                          onChange={() => field.onChange('transfer')}
                          disabled={jobsLockedByTasks}
                        />
                      )}
                    />
                    Перенести на другого пользователя
                  </label>
                  {jobsAction === 'transfer' && (
                    <div style={{ marginTop: 8 }}>
                      <Controller
                        control={control}
                        name="jobsTransferUserId"
                        rules={{ required: jobsAction === 'transfer' ? 'Выберите пользователя' : false }}
                        render={({ field, fieldState }) => (
                          <>
                            <UserSearchInput
                              name="jobs-transfer-user"
                              value={field.value}
                              selectedName={watch('jobsTransferUserName')}
                              onSelect={(id, name) => {
                                field.onChange(id);
                                setValue('jobsTransferUserName', name);
                              }}
                              onClear={() => {
                                field.onChange('');
                                setValue('jobsTransferUserName', '');
                              }}
                              placeholder="Выберите пользователя"
                              includeBlocked
                            />
                            {fieldState.error && (
                              <div className="form-field-error">{fieldState.error.message}</div>
                            )}
                          </>
                        )}
                      />
                    </div>
                  )}
                </div>
              </>
            )}

            {error && <div className="adm-form-error">{error}</div>}

            {!deleting && (
              <div className="adm-form-actions">
                <Button variant="adm-cancel" type="button" onClick={onClose}>
                  Отмена
                </Button>
                <Button variant="danger" type="submit" disabled={deleting || counts === null}>
                  <IconTrash size={15} stroke={2} />
                  Удалить
                </Button>
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
};

export { DeleteUserModal };
